"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import dayjs from "dayjs";
import { CustomerAPI } from "libs/api/customer/src/lib/CustomerAPI";
import { IFreshOptionsNewCustomerRequest } from "@api-clients/customer";
import {
  NibblesProductTitle,
  SprinklesProductTitle,
} from "libs/api/customer/src/lib/types/products";

import {
  ISignupFlowStore,
  ICatFormData,
  CatFieldName,
  getAvailableFreshPlansRequest,
  IViewState,
  IAgeViewState,
  ICatEntry,
  IUserFormData,
  IDeliveryData,
  ISubscriptionData,
  TDiscountType,
} from "../types";
import {
  defaultDelivery,
  defaultSubscription,
  defaultUser,
  defaultViewState,
  defaultAgeViewState,
} from "../constants";
import {
  getNestedFieldValue,
  updateNestedField,
  removeNestedField,
  transformCatDataForApi,
  calculateCatBirthday,
} from "../utils";
import {
  mapFreshOptionsResponseToPricingDisplay,
  transformCats,
} from "../transformer";

const customerApi = new CustomerAPI(
  // eslint-disable-next-line @typescript-eslint/dot-notation
  process.env["NEXT_PUBLIC_OLD_API_BASE_URL"] as string,
);

/**
 * Zustand store for managing sign-up flow data: cats, user, delivery, and subscription.
*/
export const useSignupFlowStore = create<ISignupFlowStore>()(
  persist(
    (set, get) => ({
      /* --- Initialisation ---*/
      isStoreInitialised: false,
      setIsStoreInitialised: (value: boolean) => set({ isStoreInitialised: value }),

      /* --- Cats ---*/
      cats: {},
      activeCatId: "",

      activeCat: () => get().cats[get().activeCatId],

      createCat: (id, cat) => set((state) => {
        if (state.cats[id]) { return {}; }
        return {
          cats: {
            ...state.cats,
            [id]: { ...cat },
          },
          viewState: {
            ...state.viewState,
            age: {
              ...state.viewState.age,
              [id]: defaultAgeViewState,
            },
          },
        };
      }),

      createCats: (entries: ICatEntry[]) => set((state) => {
        const updatedCats: Record<string, ICatFormData> = { ...state.cats };
        const updatedAgeViewState = { ...state.viewState.age };
        const incomingIds = new Set(entries.map((e) => e.id));

        // Remove cats and their view states that are no longer in the incoming entries
        for (const existingId of Object.keys(updatedCats)) {
          if (!incomingIds.has(existingId)) {
            delete updatedCats[existingId];
            delete updatedAgeViewState[existingId];
          }
        }

        // Add/update cats and initialize their age view states
        for (const { id, cat } of entries) {
          updatedCats[id] = {
            ...updatedCats[id],
            ...cat,
          };
          // Initialize age view state for new cats
          if (!updatedAgeViewState[id]) {
            updatedAgeViewState[id] = defaultAgeViewState;
          }
        }

        const firstCatId = entries[0]?.id ?? "";

        return {
          cats: updatedCats,
          activeCatId: firstCatId,
          viewState: {
            ...state.viewState,
            age: updatedAgeViewState,
          },
        };
      }),

      updateCat: (id: string, cat: Partial<ICatFormData>) => set((state) => ({
        cats: {
          ...state.cats,
          [id]: { ...state.cats[id], ...cat },
        },
      })),
      getCat: (id: string) => get().cats[id],
      getActiveCatIds: () => Object.keys(get().cats).filter((catId) => !get().cats[catId].softDelete),

      softDeleteCat: (id: string) => set((state) => ({
        cats: {
          ...state.cats,
          [id]: { ...state.cats[id], softDelete: true },
        },
      })),

      restoreCat: (id: string) => set((state) => ({
        cats: {
          ...state.cats,
          [id]: { ...state.cats[id], softDelete: false },
        },
      })),

      migrateCatData: (fieldNames: CatFieldName[], sourceId: string, targetIds: string[]) => set((state) => {
        const source = state.cats[sourceId];
        if (!source) { return {}; }

        const updatedCats = { ...state.cats };

        const updateTargetCat = (targetId: string) => {
          if (!updatedCats[targetId]) { return; }

          let target = { ...updatedCats[targetId] };

          for (const fieldName of fieldNames) {
            const sourceValue = getNestedFieldValue(source, fieldName);
            if (sourceValue !== undefined) {
              target = updateNestedField(target, fieldName, sourceValue);
            }
          }

          updatedCats[targetId] = target;
        };

        for (const targetId of targetIds) {
          updateTargetCat(targetId);
        }

        return {
          cats: updatedCats,
        };
      }),

      revertMigrateCatData: (fieldNames: CatFieldName[], targetIds: string[]) => set((state) => {
        const updatedCats = { ...state.cats };

        const removeFromTargetCat = (targetId: string) => {
          if (!updatedCats[targetId]) { return; }

          let target = { ...updatedCats[targetId] };

          for (const fieldName of fieldNames) {
            target = removeNestedField(target, fieldName);
          }

          updatedCats[targetId] = target;
        };

        for (const targetId of targetIds) {
          removeFromTargetCat(targetId);
        }

        return { cats: updatedCats };
      }),

      setActiveCatId: (id: string) => set({ activeCatId: id }),

      /* --- User ---*/
      user: defaultUser,
      updateUser: (data: Partial<IUserFormData>) => set((state) => ({
        user: {
          ...state.user,
          ...data,
        },
      })),

      /* --- Delivery ---*/
      delivery: defaultDelivery,
      updateDelivery: (data: Partial<IDeliveryData>) => set((state) => ({
        delivery: {
          ...state.delivery,
          ...data,
        },
      })),

      /* --- Subscription ---*/
      subscription: defaultSubscription,
      updateSubscription: (data: Partial<ISubscriptionData>) => set((state) => ({
        subscription: {
          ...state.subscription,
          ...data,
        },
      })),

      /* --- View State ---*/
      viewState: defaultViewState,
      updateViewState: (
        view: string,
        params: Partial<IViewState[keyof IViewState]> | Partial<IAgeViewState>,
        catId?: string,
      ) => set((state) => {
        const viewState = { ...state.viewState };

        if (view === "age" && catId) {
          // Update age viewState for specific cat
          viewState.age = {
            ...viewState.age,
            [catId]: {
              ...viewState.age[catId],
              ...params,
            } as IAgeViewState,
          };
        } else if (view === "checkout") {
          // Update global checkout viewState
          viewState.checkout = { ...viewState.checkout, ...params };

          // Keep special logic for checkout's isDeliveryComplete
          if ("isDeliveryComplete" in params && params.isDeliveryComplete === true) {
            viewState.checkout.hasCompletedDeliveryOnce = true;
          }
        }

        return { viewState };
      }),

      /* --- Reset ---*/
      resetSignupFlow: () => set({
        cats: {},
        activeCatId: "",
        user: defaultUser,
        delivery: defaultDelivery,
        subscription: defaultSubscription,
        viewState: defaultViewState,
      }),

      /* --- Call getAvailableFreshPlans and update store ---*/
      callGetAvailableFreshPlansAndUpdateStore: async (requestedProps: getAvailableFreshPlansRequest) => {
        const { user, subscription, cats, delivery } = get();

        const baseRequestObj: IFreshOptionsNewCustomerRequest = {
          deliveryDate: dayjs(delivery.deliveryDate).format("YYYY-MM-DD"),
          discountCode: subscription.discount?.discountCode,
          trialType: "TWENTY_OFF", // this will eventually be removed (tech-340 ticket)
          discountNibbles: subscription.nibblesUpsell?.added ? "FIFTY_OFF" : undefined,
          catData: transformCatDataForApi(cats, subscription),
        };

        const mergedRequestObj = { ...baseRequestObj, ...requestedProps };

        try {
          const response = await customerApi.getAvailableFreshPlans(user, mergedRequestObj);
          if (response) {
            const { deliveryDate, discount } = response;
            const activeCatIds = get().getActiveCatIds();

            const { trial,
              ongoing,
              perCatPricing,
              nibblesUpsell,
              sprinklesUpsell } = mapFreshOptionsResponseToPricingDisplay(
              response,
              activeCatIds.length,
            );

            set({
              delivery: {
                ...get().delivery,
                deliveryDate,
              },
              subscription: {
                ...subscription,
                trial: {
                  planType: "PlanTrial14x14",
                  totalOriginalPrice: trial.originalTotal,
                  totalDiscountedPrice: trial.discountedTotal,
                  originalPricePerDay: trial.originalPerCatPerDay,
                  discountedPricePerDay: trial.discountedPerCatPerDay,
                },
                ongoing: {
                  planType: "Plan28x28",
                  totalOriginalPrice: ongoing.originalTotal,
                  totalDiscountedPrice: ongoing.discountedTotal,
                  originalPricePerDay: ongoing.originalPerCatPerDay,
                  discountedPricePerDay: ongoing.discountedPerCatPerDay,
                },
                discount: {
                  discountCode: discount?.code ?? "",
                  type: (discount?.discount_type as TDiscountType) ?? "percentage",
                  value: discount?.value ?? 0,
                },
                nibblesUpsell: {
                  added: nibblesUpsell?.quantity ? nibblesUpsell.quantity > 0 : false,
                  discountedPrice: nibblesUpsell?.trial.price ?? 0,
                  originalPrice: nibblesUpsell?.ongoing.price ?? 0,
                  type: nibblesUpsell?.product as NibblesProductTitle,
                  quantity: nibblesUpsell?.quantity ?? 0,
                },
                sprinklesUpsell: {
                  added: sprinklesUpsell?.quantity ? sprinklesUpsell.quantity > 0 : false,
                  discountedPrice: sprinklesUpsell?.trial.price ?? 0,
                  originalPrice: sprinklesUpsell?.ongoing.price ?? 0,
                  type: sprinklesUpsell?.product as SprinklesProductTitle,
                  quantity: sprinklesUpsell?.quantity ?? 0,
                },
              },
              cats: transformCats(cats, perCatPricing),
            });
          }
          return response;
        } catch (error) {
          return error;
        }
      },

      /* --- Birthday calculation ---*/
      calculateAllCatsBirthdays: () => set((state) => {
        const updatedCats = { ...state.cats };

        Object.entries(updatedCats).forEach(([ catId, cat ]) => {
          if (!cat.softDelete) {
            const calculatedBirthday = calculateCatBirthday(cat);
            if (calculatedBirthday) {
              updatedCats[catId] = {
                ...cat,
                birthday: calculatedBirthday,
              };
            }
          }
        });

        return { cats: updatedCats };
      }),

      /* --- Loading state ---*/
      isLoading: false,
      setIsLoading: (state) => set({ isLoading: state }),
    }),
    {
      name: "suf-store",
      onRehydrateStorage: () => (state) => {
        state?.setIsStoreInitialised(true);
      },
    },
  ),
);
